import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  FaArrowLeft,
  FaArrowRight,
  FaCheck,
  FaExclamationCircle,
  FaSpinner,
  FaExclamationTriangle,
} from "react-icons/fa";
import { useReviewPrimaryActionContext } from "./useReviewPrimaryAction";
import { isEditableReviewShortcutTarget, useReviewNavigationContext } from "./useReviewNavigation";
import { debugReview } from "./reviewDebug";
import "./ReviewWizard.css";
import { buildAutomationChildTestId, resolveAutomationTestId } from "../InputField/testId";

const ReviewWizard = ({
  loading = false,
  error = null,
  testId,
}) => {
  const reviewNavigation = useReviewNavigationContext();
  const reviewPrimaryAction = useReviewPrimaryActionContext();
  const [inlineMessage, setInlineMessage] = useState("");
  const [pendingNavigationOffset, setPendingNavigationOffset] = useState(0);
  const [pendingCrossWizardTaskKey, setPendingCrossWizardTaskKey] = useState("");
  const [isRunningDockPageAction, setIsRunningDockPageAction] = useState(false);
  const resolvedTestId = resolveAutomationTestId(testId, "review-dock");
  const lastForwardAttemptPageKeyRef = useRef("");
  const activeForwardAttemptIdRef = useRef("");
  const currentWizardRef = useRef("");
  const currentPrimaryActionIdRef = useRef("");
  const isMountedRef = useRef(true);

  const {
    actionableTasks,
    canSubmitReview,
    completedTasks,
    currentWizard,
    currentWizardActionableTasks,
    getNextTask,
    getPreviousTask,
    goToTask,
    hasVisibleTasks,
    selectedTaskIndex,
    visibleTasks,
  } = reviewNavigation || {};

  const backAction = reviewPrimaryAction?.backAction;
  const primaryAction = reviewPrimaryAction?.primaryAction;
  const totalTasks = visibleTasks?.length || 0;
  const actionableCount = actionableTasks?.length || 0;
  const completedCount = completedTasks?.length || 0;
  const isDockNavigationPending = pendingNavigationOffset !== 0;
  const isForwardPending = pendingNavigationOffset > 0;
  const isBackwardPending = pendingNavigationOffset < 0;
  const isAnyPageActionLoading = Boolean(primaryAction?.loading || backAction?.loading);
  const isDockNavigationLocked = isDockNavigationPending || isAnyPageActionLoading || isRunningDockPageAction;
  currentWizardRef.current = currentWizard || "";
  currentPrimaryActionIdRef.current = primaryAction?.id || "";
  const currentTaskNumber = selectedTaskIndex >= 0 ? selectedTaskIndex + 1 : 0;
  const nextIssueNumber = useMemo(() => {
    if (!totalTasks) {
      return 0;
    }

    if (currentTaskNumber > 0) {
      return currentTaskNumber;
    }

    const firstActionableTask = actionableTasks?.[0];
    if (firstActionableTask?.taskKey) {
      const actionableTaskIndex = visibleTasks.findIndex((task) => task?.taskKey === firstActionableTask.taskKey);
      if (actionableTaskIndex >= 0) {
        return actionableTaskIndex + 1;
      }
    }

    return canSubmitReview ? totalTasks : 1;
  }, [actionableTasks, canSubmitReview, currentTaskNumber, totalTasks, visibleTasks]);
  const currentTaskSerialLabel = useMemo(() => {
    if (!totalTasks) {
      return "";
    }

    if (currentTaskNumber > 0) {
      return `Item ${currentTaskNumber} of ${totalTasks}`;
    }

    return canSubmitReview ? "Ready to submit" : "Not started";
  }, [canSubmitReview, currentTaskNumber, totalTasks]);
  const currentIssueLabel = currentTaskNumber > 0
    ? `Issue ${currentTaskNumber} of ${totalTasks}`
    : canSubmitReview
      ? `All ${totalTasks} reviewed`
      : `Issue ${nextIssueNumber || 1} of ${totalTasks}`;
  const completedLabel = `${completedCount} fixed`;
  const actionableLabel = actionableCount === 1
    ? "1 needs fixing"
    : `${actionableCount} need fixing`;
  const isPreviousTaskDisabled = useMemo(() => {
    if (isDockNavigationLocked || !hasVisibleTasks) {
      return true;
    }

    return selectedTaskIndex <= 0;
  }, [
    hasVisibleTasks,
    isDockNavigationLocked,
    selectedTaskIndex,
  ]);
  const isNextTaskDisabled = useMemo(() => {
    if (isDockNavigationLocked || !hasVisibleTasks) {
      return true;
    }

    if (selectedTaskIndex < 0) {
      return totalTasks <= 0;
    }

    return selectedTaskIndex >= (totalTasks - 1);
  }, [hasVisibleTasks, isDockNavigationLocked, selectedTaskIndex, totalTasks]);
  const previousDisabledReason = isDockNavigationLocked
    ? "Please wait while issue navigation finishes."
    : !hasVisibleTasks
      ? "There are no issues on this form."
      : selectedTaskIndex <= 0
        ? "This is the first issue."
        : "";
  const nextDisabledReason = isDockNavigationLocked
    ? "Please wait while issue navigation finishes."
    : !hasVisibleTasks
      ? "There are no issues on this form."
      : totalTasks <= 0 || selectedTaskIndex >= (totalTasks - 1)
        ? "This is the last issue."
        : "";
  const clearPendingCrossWizardNavigation = useCallback(() => {
    lastForwardAttemptPageKeyRef.current = "";
    activeForwardAttemptIdRef.current = "";
    setIsRunningDockPageAction(false);
    setPendingCrossWizardTaskKey("");
    setPendingNavigationOffset(0);
  }, []);

  const waitForPageTransition = useCallback(async (
    expectedPreviousWizard,
    expectedPreviousActionId,
    timeoutMs = 500
  ) => {
    const hasPageTransitioned = () => (
      (currentWizardRef.current || "") !== (expectedPreviousWizard || "")
      || (currentPrimaryActionIdRef.current || "") !== (expectedPreviousActionId || "")
    );

    if (typeof window === "undefined") {
      return hasPageTransitioned();
    }

    const startedAt = Date.now();
    while ((Date.now() - startedAt) < timeoutMs) {
      if (hasPageTransitioned()) {
        return true;
      }
      // Give React enough time to commit the next page after the page action resolves.
      await new Promise((resolve) => window.setTimeout(resolve, 25));
    }

    return hasPageTransitioned();
  }, []);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!hasVisibleTasks) {
      setInlineMessage("");
      clearPendingCrossWizardNavigation();
    }
  }, [clearPendingCrossWizardNavigation, hasVisibleTasks]);

  useEffect(() => {
    if (!pendingCrossWizardTaskKey || !hasVisibleTasks) {
      return;
    }

    const pendingTask = visibleTasks.find((task) => task?.taskKey === pendingCrossWizardTaskKey) || null;
    debugReview("review-wizard.pending-effect", {
      currentWizard,
      pendingCrossWizardTaskKey,
      pendingNavigationOffset,
      pendingTaskFieldId: pendingTask?.fieldId,
      pendingTaskWizard: pendingTask?.wizard,
      primaryActionId: primaryAction?.id,
      primaryActionHidden: primaryAction?.hidden,
      primaryActionDisabled: primaryAction?.disabled,
      primaryActionLoading: primaryAction?.loading,
      primaryActionNotReady: primaryAction?.notReady,
      isRunningDockPageAction,
      lastForwardAttemptPageKey: lastForwardAttemptPageKeyRef.current,
    });
    if (!pendingTask) {
      debugReview("review-wizard.pending-missing-task", {
        currentWizard,
        pendingCrossWizardTaskKey,
      });
      clearPendingCrossWizardNavigation();
      return;
    }

    if ((pendingTask?.wizard || "") === (currentWizard || "")) {
      debugReview("review-wizard.pending-target-reached", {
        currentWizard,
        pendingTaskFieldId: pendingTask?.fieldId,
        pendingTaskWizard: pendingTask?.wizard,
      });
      goToTask?.(pendingTask);
      clearPendingCrossWizardNavigation();
      return;
    }

    if (pendingNavigationOffset <= 0) {
      return;
    }

    if (isRunningDockPageAction) {
      return;
    }

    const pageAction = primaryAction;
    if (!pageAction || pageAction.loading) {
      return;
    }

    if (pageAction.notReady) {
      setInlineMessage(pageAction.pendingReason || "Please wait while this page finishes preparing.");
      debugReview("review-wizard.pending-waiting-ready", {
        currentWizard,
        pendingTaskFieldId: pendingTask?.fieldId,
        pendingTaskWizard: pendingTask?.wizard,
        primaryActionId: pageAction?.id,
        pendingReason: pageAction?.pendingReason,
      });
      return;
    }

    if (pageAction.hidden || typeof pageAction.onAction !== "function") {
      if (!(currentWizardActionableTasks?.length)) {
        debugReview("review-wizard.pending-direct-jump-no-page-action", {
          currentWizard,
          pendingTaskFieldId: pendingTask?.fieldId,
          pendingTaskWizard: pendingTask?.wizard,
          primaryActionId: pageAction?.id,
        });
        goToTask?.(pendingTask);
        clearPendingCrossWizardNavigation();
        return;
      }

      clearPendingCrossWizardNavigation();
      setInlineMessage("This page cannot move to the next issue automatically right now.");
      debugReview("review-wizard.pending-no-page-action", {
        currentWizard,
        pendingTaskFieldId: pendingTask?.fieldId,
        pendingTaskWizard: pendingTask?.wizard,
        primaryActionId: pageAction?.id,
      });
      return;
    }

    if (pageAction.disabled) {
      clearPendingCrossWizardNavigation();
      setInlineMessage("Complete this page before moving to the next issue.");
      debugReview("review-wizard.pending-disabled", {
        currentWizard,
        pendingTaskFieldId: pendingTask?.fieldId,
        pendingTaskWizard: pendingTask?.wizard,
        primaryActionId: pageAction?.id,
      });
      return;
    }

    const currentActionId = pageAction?.id || "__review-action__";
    const currentPageKey = `${currentWizard || ""}:${currentActionId}`;
    if (lastForwardAttemptPageKeyRef.current === currentPageKey) {
      return;
    }

    const wizardBeforeAction = currentWizard || "";
    const attemptId = `${currentPageKey}:${Date.now()}`;
    lastForwardAttemptPageKeyRef.current = currentPageKey;
    activeForwardAttemptIdRef.current = attemptId;
    setIsRunningDockPageAction(true);

    Promise.resolve(pageAction.onAction?.())
      .then(async (actionResult) => {
        debugReview("review-wizard.page-action-resolved", {
          currentWizard: wizardBeforeAction,
          primaryActionId: currentActionId,
          pendingTaskFieldId: pendingTask?.fieldId,
          pendingTaskWizard: pendingTask?.wizard,
          actionResult,
        });
        if (
          !isMountedRef.current
          || activeForwardAttemptIdRef.current !== attemptId
        ) {
          return;
        }

        const didPageChange = await waitForPageTransition(wizardBeforeAction, currentActionId, 1200);
        if (
          !isMountedRef.current
          || activeForwardAttemptIdRef.current !== attemptId
        ) {
          return;
        }

        if (didPageChange) {
          setIsRunningDockPageAction(false);
          setInlineMessage("");
          debugReview("review-wizard.page-action-page-changed", {
            previousWizard: wizardBeforeAction,
            currentWizard: currentWizardRef.current,
            previousActionId: currentActionId,
            currentActionId: currentPrimaryActionIdRef.current,
            pendingTaskFieldId: pendingTask?.fieldId,
            pendingTaskWizard: pendingTask?.wizard,
          });
          return;
        }

        if (actionResult?.status === 400) {
          debugReview("review-wizard.page-action-blocked", {
            currentWizard: wizardBeforeAction,
            primaryActionId: currentActionId,
            pendingTaskFieldId: pendingTask?.fieldId,
            pendingTaskWizard: pendingTask?.wizard,
            actionResult,
          });
          clearPendingCrossWizardNavigation();
          return;
        }

        setIsRunningDockPageAction(false);
        if (typeof window === "undefined") {
          clearPendingCrossWizardNavigation();
          return;
        }

        window.setTimeout(() => {
          if (
            !isMountedRef.current
            || activeForwardAttemptIdRef.current !== attemptId
          ) {
            return;
          }

          if (
            (currentWizardRef.current || "") !== wizardBeforeAction
            || (currentPrimaryActionIdRef.current || "") !== currentActionId
          ) {
            return;
          }

          debugReview("review-wizard.page-action-timeout-clear", {
            currentWizard: wizardBeforeAction,
            primaryActionId: currentActionId,
            pendingTaskFieldId: pendingTask?.fieldId,
            pendingTaskWizard: pendingTask?.wizard,
          });
          clearPendingCrossWizardNavigation();
        }, 1200);
      })
      .catch(() => {
        if (
          !isMountedRef.current
          || activeForwardAttemptIdRef.current !== attemptId
        ) {
          return;
        }

        clearPendingCrossWizardNavigation();
        setInlineMessage("We couldn't move to the next issue from this page right now. Please try again.");
        debugReview("review-wizard.page-action-error", {
          currentWizard: wizardBeforeAction,
          primaryActionId: currentActionId,
          pendingTaskFieldId: pendingTask?.fieldId,
          pendingTaskWizard: pendingTask?.wizard,
        });
      });
  }, [
    currentWizard,
    currentWizardActionableTasks,
    clearPendingCrossWizardNavigation,
    goToTask,
    hasVisibleTasks,
    isRunningDockPageAction,
    pendingNavigationOffset,
    pendingCrossWizardTaskKey,
    primaryAction,
    waitForPageTransition,
    visibleTasks,
  ]);

  const handleTaskNavigation = useCallback(async (offset) => {
    if (isDockNavigationLocked) {
      return;
    }

    if (offset < 0 && isPreviousTaskDisabled) {
      return;
    }

    const targetTask = offset > 0 ? getNextTask?.() : getPreviousTask?.();
    if (!targetTask) {
      return;
    }

    debugReview("review-wizard.navigate.click", {
      offset,
      currentWizard,
      currentTaskNumber,
      targetTaskFieldId: targetTask?.fieldId,
      targetTaskWizard: targetTask?.wizard,
      primaryActionId: primaryAction?.id,
      primaryActionLoading: primaryAction?.loading,
      primaryActionDisabled: primaryAction?.disabled,
      primaryActionNotReady: primaryAction?.notReady,
    });

    if (offset < 0) {
      setInlineMessage("");
      clearPendingCrossWizardNavigation();
      goToTask?.(targetTask);
      return;
    }

    if (!targetTask?.wizard || targetTask.wizard === currentWizard) {
      setInlineMessage("");
      clearPendingCrossWizardNavigation();
      goToTask?.(targetTask);
      return;
    }

    setInlineMessage("");
    clearPendingCrossWizardNavigation();
    lastForwardAttemptPageKeyRef.current = "";
    setPendingCrossWizardTaskKey(targetTask?.taskKey || "");
    setPendingNavigationOffset(offset);
  }, [
    clearPendingCrossWizardNavigation,
    getNextTask,
    getPreviousTask,
    goToTask,
    currentTaskNumber,
    isDockNavigationLocked,
    isPreviousTaskDisabled,
    primaryAction,
    currentWizard,
  ]);

  useEffect(() => {
    if (!hasVisibleTasks || typeof window === "undefined") {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (!window.matchMedia("(min-width: 992px)").matches) {
        return;
      }

      if (!event.altKey || !event.shiftKey || isEditableReviewShortcutTarget(event.target)) {
        return;
      }

      if (event.key === "ArrowUp") {
        event.preventDefault();
        handleTaskNavigation(-1);
      } else if (event.key === "ArrowDown") {
        event.preventDefault();
        handleTaskNavigation(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleTaskNavigation, hasVisibleTasks]);

  if (loading) {
    return (
      <div className="fv_review_bar fv_review_bar--loading" aria-busy="true" data-testid={buildAutomationChildTestId(resolvedTestId, "loading")}>
        <div className="fv_review_bar__dock fv_review_bar__dock--loading" data-testid={buildAutomationChildTestId(resolvedTestId, "loading-dock")}>
          <div className="fv_review_bar__dock_status">
            <span className="fv_review_bar__dock_icon fv_review_bar__dock_icon--loading">
              <FaSpinner />
            </span>
            <div className="fv_review_bar__dock_copy">
              <div className="fv_review_bar__dock_status_text">Loading review navigator.</div>
            </div>
          </div>
          <div className="fv_review_bar__dock_skeletons">
            <Skeleton height={18} width={66} borderRadius={999} />
            <Skeleton height={38} width={38} borderRadius={999} />
            <Skeleton height={38} width={38} borderRadius={999} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fv_review_bar fv_review_bar--error" role="alert" data-testid={buildAutomationChildTestId(resolvedTestId, "error")}>
        <div className="fv_review_bar__dock" data-testid={buildAutomationChildTestId(resolvedTestId, "error-dock")}>
          <div className="fv_review_bar__dock_status">
            <span className="fv_review_bar__dock_icon fv_review_bar__dock_icon--error">
              <FaExclamationTriangle />
            </span>
            <div className="fv_review_bar__dock_copy">
              <div className="fv_review_bar__dock_status_text">Review navigator unavailable right now.</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!hasVisibleTasks) {
    return null;
  }

  const previousButtonTitle = isPreviousTaskDisabled ? previousDisabledReason : "Go to previous issue";
  const nextButtonTitle = isNextTaskDisabled ? nextDisabledReason : "Go to next issue";

  return (
    <>
      <div className="fv_review_bar" aria-busy={isDockNavigationLocked} data-testid={resolvedTestId}>
        {inlineMessage ? (
          <div className="fv_review_bar__inline_notice" role="alert" aria-live="polite" data-testid={buildAutomationChildTestId(resolvedTestId, "notice")}>
            {inlineMessage}
          </div>
        ) : null}

        <div className="fv_review_bar__dock" data-testid={buildAutomationChildTestId(resolvedTestId, "bar")}>
          <div className="fv_review_bar__dock_status fv_review_bar__dock_status--metrics">
            <div className="fv_review_bar__dock_copy">
              <div
                className="fv_review_bar__dashboard"
                aria-label={`Current item ${currentTaskSerialLabel}, ${completedCount} done, ${actionableCount} open`}
                data-testid={buildAutomationChildTestId(resolvedTestId, "metrics")}
              >
                <div className="fv_review_bar__metric_chip fv_review_bar__metric_chip--current" data-testid={buildAutomationChildTestId(resolvedTestId, "current-issue")}>
                  <span className="fv_review_bar__metric_badge" aria-hidden="true">
                    {canSubmitReview ? <FaCheck /> : (nextIssueNumber || 1)}
                  </span>
                  <span className="fv_review_bar__metric_text">{currentIssueLabel}</span>
                </div>

                <span className="fv_review_bar__metric_divider" aria-hidden="true">&bull;</span>

                <div className="fv_review_bar__metric_chip fv_review_bar__metric_chip--done" data-testid={buildAutomationChildTestId(resolvedTestId, "completed-count")}>
                  <span className="fv_review_bar__metric_icon" aria-hidden="true">
                    <FaCheck />
                  </span>
                  <span className="fv_review_bar__metric_text">{completedLabel}</span>
                </div>

                <span className="fv_review_bar__metric_divider" aria-hidden="true">&bull;</span>

                <div className="fv_review_bar__metric_chip fv_review_bar__metric_chip--open" data-testid={buildAutomationChildTestId(resolvedTestId, "open-count")}>
                  <span className="fv_review_bar__metric_icon" aria-hidden="true">
                    <FaExclamationCircle />
                  </span>
                  <span className="fv_review_bar__metric_text">{actionableLabel}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="fv_review_bar__dock_actions" data-testid={buildAutomationChildTestId(resolvedTestId, "actions")}>
            <span title={previousButtonTitle}>
              <button
                type="button"
                className="fv_review_bar__icon_btn"
                data-testid={buildAutomationChildTestId(resolvedTestId, "previous-issue")}
                onClick={() => { handleTaskNavigation(-1); }}
                aria-label={previousButtonTitle}
                title={previousButtonTitle}
                disabled={isPreviousTaskDisabled}
              >
                {isBackwardPending ? <FaSpinner className="fv_review_bar__icon_spinner" /> : <FaArrowLeft />}
                <span className="fv_review_bar__icon_btn_label">Prev</span>
              </button>
            </span>

            <span title={nextButtonTitle}>
              <button
                type="button"
                className="fv_review_bar__icon_btn"
                data-testid={buildAutomationChildTestId(resolvedTestId, "next-issue")}
                onClick={() => { handleTaskNavigation(1); }}
                aria-label={nextButtonTitle}
                title={nextButtonTitle}
                disabled={isNextTaskDisabled}
              >
                <span className="fv_review_bar__icon_btn_label">Next Issue</span>
                {isForwardPending ? <FaSpinner className="fv_review_bar__icon_spinner" /> : <FaArrowRight />}
              </button>
            </span>

            {/* Bottom sheet toggle is temporarily disabled. */}
          </div>
        </div>
      </div>

      {/* Bottom sheet is temporarily disabled. */}
    </>
  );
};

export default ReviewWizard;
